import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAuth } from 'firebase/auth';
import { translations } from '../translations';
import { useNavigate } from 'react-router-dom';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useUserData } from '../contexts/UserDataContext';
import { api } from '../services/api';
import { makeApiRequest, API_CONFIG } from '../config/environment';

const languages = [
    { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
    { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
    { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
    { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
    { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
    { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
    { code: 'en', name: 'English', nativeName: 'English' },
];

interface SignupProps {
    onSwitchToLogin: () => void;
    onClose: () => void;
    selectedLanguage?: string;
}

export default function Signup({ onSwitchToLogin, onClose, selectedLanguage = '' }: SignupProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [lang, setLang] = useState(selectedLanguage);
    const [successMessage, setSuccessMessage] = useState('');
    // Use English for translations regardless of selected language to avoid fallback issues
    const t = translations.en;
    const { signup } = useAuth();
    const { refreshUserData, userData } = useUserData();
    const navigate = useNavigate();

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        if (!lang) {
            setError('Please select your native language');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        try {
            setLoading(true);
            setError('');

            // Clear any stored language preference to ensure selected language is used
            localStorage.removeItem('selectedLanguage');

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            const userId = user.uid;

            // Create user in backend using centralized API configuration
            try {
                // First try to get user data to check if exists
                await api.getUserData(userId);
                // If user exists, just refresh data and redirect
                await refreshUserData();
                window.location.href = '/home';
                return;
            } catch (error: any) {
                // User doesn't exist, create new user
                if (error.response?.status === 404) {
                    const createResponse = await makeApiRequest(API_CONFIG.ENDPOINTS.USER, {
                        method: 'POST',
                        body: JSON.stringify({
                            userId: userId,
                            selectedLanguage: lang
                        })
                    });

                    if (createResponse.ok) {
                        await refreshUserData();
                        window.location.href = '/home';
                        return;
                    } else {
                        const errorData = await createResponse.json();
                        setError('Failed to create user account. Please try again.');
                        console.log('sign up failed errorData--->', errorData);
                    }
                } else {
                    throw error;
                }
            }
        } catch (error: any) {
            console.error('Email signup error:', error);
            setError(error.message || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    }

    const handleGoogleSignUp = async () => {
        if (!lang) {
            setError('Please select your native language before signing up with Google');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setSuccessMessage('');

            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            const userId = user.uid;

            // Create user in backend with selected language using centralized config
            const createResponse = await makeApiRequest(API_CONFIG.ENDPOINTS.USER, {
                method: 'POST',
                body: JSON.stringify({
                    userId: userId,
                    selectedLanguage: lang  // Use selected language from dropdown
                })
            });

            if (createResponse.ok) {
                // Refresh user data to load the newly created user
                await refreshUserData();
                setSuccessMessage('Account created successfully! Welcome to your English learning journey...');
                setTimeout(() => {
                    window.location.href = '/home';
                }, 2000);
            } else {
                const errorData = await createResponse.json();
                setError('Failed to create user account. Please try again.');
                console.log('Google signup failed errorData--->', errorData);
            }
        } catch (error: any) {
            setError(error.message || 'Failed to sign up with Google');
        } finally {
            setLoading(false);
        }
    };

    // Show loading spinner if loading state is active
    if (loading) {
        return (
            <div className="fixed inset-0 bg-gradient-to-br from-blue-900/50 via-purple-900/50 to-pink-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white/90 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 max-w-sm w-full p-8 text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        Creating Your Account...
                    </h3>
                    <p className="text-gray-600">
                        Setting up your learning experience...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-900/50 via-purple-900/50 to-pink-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 min-h-screen">
            <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 w-full max-w-sm mx-4 relative overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Decorative background elements */}
                <div className="absolute -top-10 -right-10 w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full opacity-20 blur-2xl"></div>
                <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-br from-blue-400 to-pink-500 rounded-full opacity-20 blur-2xl"></div>

                {/* Header */}
                <div className="relative z-10 p-4 sm:p-6">
                    <div className="text-center mb-6 sm:mb-8">
                        <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 animate-bounce-slow">🌟</div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-pink-600 via-purple-500 to-blue-500 bg-clip-text text-transparent mb-2">
                            {t.landing?.signup || 'Join the Journey'}
                        </h2>
                        <p className="text-gray-600 text-base sm:text-lg">
                            {'Create your account and start mastering English today'}
                        </p>
                    </div>

                    {successMessage && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
                            <div className="flex items-center mb-2">
                                <span className="text-green-500 mr-2">✅</span>
                                <span className="font-semibold">Success!</span>
                            </div>
                            <p className="text-sm">{successMessage}</p>
                        </div>
                    )}

                    <button
                        onClick={handleGoogleSignUp}
                        disabled={loading || !lang}
                        className={`w-full flex items-center justify-center gap-2 border rounded-xl py-3 mb-6 shadow transition-all font-semibold text-sm sm:text-base ${!lang
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        style={{ boxShadow: '0 2px 8px 0 rgba(66,133,244,0.08)' }}
                    >
                        <img src="/google-icon.svg" alt="Google" style={{ width: 20, height: 20 }} className="sm:w-6 sm:h-6" />
                        <span>Sign up with Google</span>
                    </button>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                            <div className="flex items-center mb-2">
                                <span className="text-red-500 mr-2">⚠️</span>
                                <span className="font-semibold">Signup Error</span>
                            </div>
                            <p className="text-sm">{error}</p>
                            <button
                                onClick={() => setError('')}
                                className="text-red-600 hover:text-red-800 text-sm font-semibold mt-2"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-gray-700 text-sm font-semibold mb-2 sm:mb-3 flex items-center">
                            <span className="mr-2">🌐</span>
                            Select Native Language *
                        </label>
                        <select
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/80 backdrop-blur-sm transition-all duration-200 text-sm sm:text-base"
                            value={lang}
                            onChange={e => setLang(e.target.value)}
                            required
                        >
                            <option value="">Select your native language</option>
                            {languages.map(l => (
                                <option key={l.code} value={l.code}>{l.name} ({l.nativeName})</option>
                            ))}
                        </select>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-gray-700 text-sm font-semibold mb-3 flex items-center">
                                <span className="mr-2">📧</span>
                                {t.landing?.email || 'Email Address'}
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200"
                                placeholder={t.landing?.email || 'Enter your email'}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-semibold mb-3 flex items-center">
                                <span className="mr-2">🔒</span>
                                {t.landing?.password || 'Password'}
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200"
                                placeholder={t.landing?.password || 'Create a password'}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-semibold mb-3 flex items-center">
                                <span className="mr-2">✅</span>
                                {t.landing?.confirmPassword || 'Confirm Password'}
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200"
                                placeholder={t.landing?.confirmPassword || 'Confirm your password'}
                                required
                            />
                        </div>

                        <div className="flex space-x-4 pt-4">
                            <button
                                type="submit"
                                disabled={loading || !lang}
                                className={`flex-1 py-3 px-6 rounded-xl font-semibold transition-all duration-200 shadow-lg ${!lang
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600 hover:shadow-xl transform hover:scale-105'
                                    }`}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <span className="animate-spin mr-2">⏳</span>
                                        {'Creating account...'}
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        <span className="mr-2">🚀</span>
                                        {t.landing?.signup || 'Create Account'}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200"
                            >
                                {'Cancel'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-gray-600 mb-4">
                            {'Already have an account?'}
                        </p>
                        <button
                            onClick={onSwitchToLogin}
                            className="text-pink-600 hover:text-pink-800 font-semibold text-lg transition-colors duration-200 hover:scale-105 inline-flex items-center"
                        >
                            <span className="mr-2">🔐</span>
                            {t.landing?.login || 'Sign In'}
                        </button>
                    </div>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none transition-colors z-20"
                    aria-label="Close"
                >
                    ×
                </button>
            </div>
        </div>
    );
}